/**
 * Agent console (texting-agent plan §7.1 — step 4, copilot v1; §7.1b
 * conversation workspace, 2026-07-28).
 *
 * The BOARD is a master–detail workspace: a compact conversation list on the
 * left, and — without ever leaving the console — the full two-way SMS thread
 * on the right (lead + agent + human + automation + the agent's own timeline
 * notes), with that conversation's pending drafts and open escalations
 * actionable inline. The owner's complaint this answers: "I have to flip
 * between the leads page and the console to see both directions."
 *
 * DRAFTS (the copilot heart — approve / edit / reject, oldest first) and
 * ESCALATIONS keep their queue tabs for cross-conversation triage; both link
 * back into the workspace. Escalations carry plain-language guidance and the
 * Claim/Resolve verbs are explained where they sit (Claim = I'm on it, stops
 * the agent's countdown; Resolve = handled, clears the queue).
 *
 * Live via the ids-only `crm:agent` subscription with a slow poll fallback
 * (the CommsThread pattern) — every ping re-fetches the authed lists; no PHI
 * ever rides the broadcast.
 */
import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bot, MessageSquareText, AlertTriangle,
  PauseCircle, PlayCircle, Hand, Loader2,
  PhoneCall, XCircle, ArrowLeft, ExternalLink, User, Clock,
} from 'lucide-react';
import api from '../../../services/api';
import { useNow } from '../../useNow';
import { useAgentConsoleFeed, type FeedReason } from '../../useAgentConsoleFeed';
import PageHeader, { RefreshButton } from '../../components/PageHeader';
import { fieldCls } from '../../components/filters';
import { RowLink } from '../../components/shared';
import type { StudyOption } from '../../types';
import {
  ESCALATION_KIND_LABEL as KIND_LABEL,
  escalationGuide,
  EscalationBreakdown,
} from '../../components/escalationDisplay';
import DraftCard from '../../components/agent/DraftCard';
import EscalationContext from '../../components/agent/EscalationContext';
import {
  ago, inFuture, timeShort, btnGhost, btnDanger, iconBtn,
  STATUS_PILL, SENDER_LABEL, type DraftRow, type ThreadMessage,
} from '../../components/agent/shared';
import type { QueueDetermination, QueueEscalation, QueueReview } from './AgentReviewQueue';
import type { BatchRow, BatchTargetRow } from './BatchesPanel';
import { Alert } from '../../components/ui';
import { isQueueKind } from './v2/needsYou';
// The console surface (D-7 IA + D-8 lanes). This page owns the data and the
// actions; ConsoleV2 owns the layout.
import ConsoleV2 from './v2/ConsoleV2';

/**
 * The "just replied" window. During a busy hour the live grid's real question
 * is "who is talking to us RIGHT NOW" — a quarter of an hour is long enough to
 * still catch a lead mid-exchange (the agent's own reply delay plus a human's
 * thinking time) and short enough that everything it shows is still live.
 */
const RECENT_REPLY_MINUTES = 15;

export interface BoardRow {
  id: string;
  contact_id: string;
  contact_name: string | null;
  study_name: string | null;
  status: string;
  pause_reason: string | null;
  origin: string;
  mode: string;
  phase: string | null;
  turns: number;
  messages_sent: number;
  cost_usd: number;
  /** C-10: next booked callback (ISO) — same field the AgentPanel chip reads. */
  upcoming_callback_at: string | null;
  /** C-15: screen progress n/m (null until the study has criteria to count). */
  screen_progress: { answered: number; total: number } | null;
  open_flags: number;
  last_message: { body: string; direction: string; occurred_at: string } | null;
  /** The lead's newest reply INSIDE this conversation — null = they have never
   *  answered it. Drives the two lead-reply filters (and their ordering). */
  last_lead_reply_at: string | null;
  /** The "agent is writing" pulse, TTL-expired server-side — drives the live
   *  grid's per-tile pulse so a supervisor can see which threads are moving. */
  drafting_since: string | null;
  created_at: string;
}

export interface EscalationRow {
  id: string;
  conversation_id: string;
  contact_id?: string | null;
  contact_name?: string | null;
  kind: string;
  status: string;
  claimed_by: string | null;
  payload: any;
  created_at: string;
}

export interface Metrics {
  active_conversations: number;
  drafts_waiting: number;
  escalations_open: number;
  approvals_today: number;
  approval_untouched_rate: number | null;
  spend_today_usd: number;
}

interface DetailTurn {
  id: string;
  conversation_id?: string;
  session_id: string | null;
  trigger: string;
  status: string;
  draft_body: string | null;
  final_body: string | null;
  validator_results: any;
  actions: any;
  error: string | null;
  created_at: string;
  sent_at: string | null;
  outbound_communication_id: string | null;
}

interface ConversationDetail {
  conversation: {
    id: string;
    contact_id: string;
    contact_name: string | null;
    contact_phone: string | null;
    status: string;
    pause_reason: string | null;
    close_reason: string | null;
    mode_at_open: string;
    cost_usd: number;
    upcoming_callback_at: string | null;
    created_at: string;
  } | null;
  sessions: Array<{ id: string; study_id: string | null; study_name: string | null; phase: string | null; ended_at: string | null; end_reason: string | null }>;
  turns: DetailTurn[];
  escalations: EscalationRow[];
  messages: ThreadMessage[];
}

// ---------------------------------------------------------------------------
// Escalation card — the queue tab and the workspace share it.
// ---------------------------------------------------------------------------

export function EscalationCard({
  e, busy, nowTs, showContact = false, showGuide = true, showBreakdown = false, onAction, onViewThread,
}: {
  e: EscalationRow;
  busy: boolean;
  nowTs: number;
  showContact?: boolean;
  showGuide?: boolean;
  /** The escalations tab shows the full breakdown (verbatim + why + rule). */
  showBreakdown?: boolean;
  onAction: (id: string, action: 'claim' | 'resolve') => Promise<void>;
  onViewThread?: () => void;
}) {
  const navigate = useNavigate();
  /** §6.7b: an open human-first escalation is holding the agent's own reply. */
  const hold = e.status === 'open' && e.payload?.release_at ? (() => {
    const left = new Date(String(e.payload.release_at)).getTime() - nowTs;
    if (left <= 0) return { text: 'agent replied', pending: false };
    const secs = Math.ceil(left / 1000);
    return { text: `agent replies in ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`, pending: true };
  })() : null;

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <AlertTriangle className={`h-4 w-4 flex-shrink-0 ${e.kind === 'hostile_or_distress' ? 'text-amber-500' : 'text-gray-400'}`} />
        {showContact && (
          <button
            type="button"
            onClick={() => e.contact_id && navigate(`/crm/contacts/${e.contact_id}`)}
            className="inline-flex min-h-[44px] max-w-full items-center truncate text-sm font-medium text-gray-900 hover:text-primary-700 dark:text-gray-100 dark:hover:text-primary-400 sm:min-h-0"
          >
            {e.contact_name || 'Lead'}
          </button>
        )}
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
          {KIND_LABEL[e.kind] || e.kind}
        </span>
        {e.status === 'claimed' && (
          <span
            className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
            title="Someone is on it — resolve it when it's handled"
          >
            claimed
          </span>
        )}
        {hold && (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${hold.pending
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
              : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}
            title="The agent escalated to a person first. If nobody claims it before the countdown ends, the agent answers the lead itself."
          >
            {hold.text}
          </span>
        )}
        {/* Phones give the reason its own full-width line — at 375px a
            360px-wide truncating span crowded every chip beside it off the row. */}
        {e.payload?.reason && (
          <span className="w-full min-w-0 truncate text-xs text-gray-500 dark:text-gray-400 sm:w-auto sm:max-w-[360px]">{String(e.payload.reason)}</span>
        )}
        <span className="text-xs text-gray-400">{ago(e.created_at)}</span>
        {/* Claim / Resolve / Thread drop to their own row on phones instead of
            being squeezed against the chips. */}
        <span className="inline-flex w-full basis-full items-center justify-end gap-1.5 sm:ml-auto sm:w-auto sm:basis-auto">
          {onViewThread && (
            <button
              type="button"
              onClick={onViewThread}
              title="Open this conversation's full thread in the console"
              className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
            >
              <MessageSquareText className="h-3.5 w-3.5" /> Thread
            </button>
          )}
          {e.status !== 'resolved' && (
            <>
              {e.status === 'open' && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onAction(e.id, 'claim')}
                  title="I'm on it — assigns this to you, tells teammates it's taken, and stops any agent countdown"
                  className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                >
                  Claim
                </button>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => onAction(e.id, 'resolve')}
                title="It's handled — clears this from the queue (does not text the lead)"
                className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50 sm:min-h-0"
              >
                Resolve
              </button>
            </>
          )}
        </span>
      </div>
      {/* The full breakdown (escalations tab): verbatim words, the screening
          question + answer + rule it broke, and the AI's concern — from the
          enriched payload. */}
      {/* The icon-gutter indent is desktop-only — on a phone it just narrows
          the explanatory copy that most needs the width. */}
      {showBreakdown && (
        <div className="sm:pl-6">
          <EscalationBreakdown e={e} />
        </div>
      )}
      {/* …and beneath the summary, the verbatim exchange that produced it,
          fetched live on demand. The breakdown above is a frozen snapshot
          (deduped flags keep the FIRST wording); this shows what was really
          said, including the re-phrasings the snapshot cannot carry. */}
      {showBreakdown && <EscalationContext escalationId={e.id} />}
      {showGuide && e.status !== 'resolved' && escalationGuide(e) && (
        <p className="mt-1.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400 sm:pl-6">{escalationGuide(e)}</p>
      )}
      {e.payload?.guidance && e.status !== 'resolved' && (
        <p className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-400 sm:pl-6">{String(e.payload.guidance)}</p>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// §7.1b — the conversation workspace (thread + drafts + escalations, inline).
// ---------------------------------------------------------------------------

/** Thread markers for turns that never became a visible message — the "what
 *  is going on" layer: why the agent went quiet, what got blocked, what died. */
const turnMarker = (t: DetailTurn): { text: string; tone: 'red' | 'amber' | 'gray' } | null => {
  if (t.status === 'suppressed') {
    if (String(t.error || '').startsWith('human_first_')) return null; // its system note already tells the story
    return { text: 'Agent reply blocked by guardrails — salvage it from the draft queue if it should still go', tone: 'red' };
  }
  if (t.status === 'failed') return { text: `Send failed${t.error ? ` (${String(t.error).slice(0, 80)})` : ''} — approve again below to retry`, tone: 'red' };
  if (t.status === 'superseded') return { text: 'A drafted reply was set aside — the lead texted first, and the agent re-drafted with their newest message in view', tone: 'gray' };
  if (t.status === 'escalated_only' && t.actions?.handoff) return { text: 'Screen complete — handed off for human review', tone: 'amber' };
  if (t.status === 'escalated_only' && t.actions?.pivot) return { text: `Pivot started (${String(t.actions.pivot).replace(/_/g, ' ')})`, tone: 'amber' };
  return null;
};

export function ConversationWorkspace({
  conversationId, refreshTick, nowTs, busyId, onBack, onConversationAction, onEscalationAction, onApprove, onReject,
}: {
  conversationId: string;
  refreshTick: number;
  nowTs: number;
  busyId: string | null;
  onBack: () => void;
  onConversationAction: (id: string, action: 'pause' | 'resume' | 'takeover' | 'close', body?: { reason?: string }) => Promise<void>;
  onEscalationAction: (id: string, action: 'claim' | 'resolve') => Promise<void>;
  onApprove: (t: DraftRow, text?: string) => Promise<void>;
  onReject: (t: DraftRow, reason: string) => Promise<void>;
}) {
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const threadRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get<ConversationDetail>(`/api/crm/agent/conversations/${conversationId}`)
      .then((res) => { if (alive) setDetail(res.data); })
      .catch((err) => { console.error('[AgentConsole] detail fetch failed:', err); if (alive) setDetail(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [conversationId, refreshTick]);

  // Keep the newest message in view — the thread reads bottom-up like any
  // messaging app.
  const messageCount = detail?.messages?.length || 0;
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [conversationId, messageCount, loading]);

  if (loading && !detail) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white py-16 dark:border-gray-700 dark:bg-gray-800">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
      </div>
    );
  }
  const convo = detail?.conversation;
  if (!convo) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
        Conversation not found — it may have been merged or removed.
      </div>
    );
  }

  const currentSession = (detail!.sessions || []).filter((s) => !s.ended_at).slice(-1)[0] || (detail!.sessions || []).slice(-1)[0] || null;
  const openEscalations = (detail!.escalations || []).filter((e) => e.status === 'open' || e.status === 'claimed');
  const pendingDrafts: DraftRow[] = (detail!.turns || [])
    .filter((t) => t.status === 'drafted' || t.status === 'suppressed')
    .map((t) => ({
      id: t.id, conversation_id: conversationId, status: t.status,
      draft_body: t.draft_body || '', validator_results: t.validator_results, created_at: t.created_at,
    }));
  // Approved-but-unsent = the reply is on its way (humanized delay, quiet
  // hours, or a human-first window) — say so instead of looking idle.
  const inFlight = (detail!.turns || []).filter((t) => (t.status === 'approved' || t.status === 'sending') && !t.outbound_communication_id);

  // Interleave messages with the never-sent turn markers by time.
  const items: Array<{ at: string; key: string; el: ReactNode }> = [];
  for (const m of detail!.messages || []) {
    if (m.sender === 'system') {
      items.push({
        at: m.occurred_at,
        key: `m-${m.id}`,
        el: (
          <div className="flex justify-center py-0.5">
            <span className="max-w-[85%] rounded-full bg-gray-100 px-3 py-1 text-center text-[11px] text-gray-500 dark:bg-gray-700/60 dark:text-gray-400">
              {m.body} · {timeShort(m.occurred_at)}
            </span>
          </div>
        ),
      });
      continue;
    }
    const isLead = m.sender === 'lead';
    items.push({
      at: m.occurred_at,
      key: `m-${m.id}`,
      el: (
        <div className={`flex ${isLead ? 'justify-start' : 'justify-end'}`}>
          <div className={`max-w-[82%] ${isLead ? '' : 'text-right'}`}>
            <div className={`inline-block whitespace-pre-wrap rounded-2xl px-3 py-2 text-left text-sm ${isLead
              ? 'rounded-bl-sm bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
              : m.sender === 'agent'
                ? 'rounded-br-sm bg-primary-600 text-white'
                : m.sender === 'human'
                  ? 'rounded-br-sm bg-primary-100 text-primary-950 dark:bg-primary-900/50 dark:text-primary-100'
                  : 'rounded-br-sm border border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}
            >
              {m.body}
            </div>
            <div className={`mt-0.5 flex items-center gap-1.5 text-[10px] text-gray-400 ${isLead ? '' : 'justify-end'}`}>
              {m.sender === 'agent' && <Bot className="h-3 w-3" />}
              {m.sender === 'human' && <User className="h-3 w-3" />}
              <span className="font-medium">{SENDER_LABEL[m.sender] || m.sender}</span>
              <span>{timeShort(m.occurred_at)}</span>
            </div>
          </div>
        </div>
      ),
    });
  }
  for (const t of detail!.turns || []) {
    const marker = turnMarker(t);
    if (!marker) continue;
    const tone = marker.tone === 'red'
      ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
      : marker.tone === 'amber'
        ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
        : 'bg-gray-100 text-gray-500 dark:bg-gray-700/60 dark:text-gray-400';
    items.push({
      at: t.created_at,
      key: `t-${t.id}`,
      el: (
        <div className="flex justify-center py-0.5">
          <span className={`max-w-[85%] rounded-full px-3 py-1 text-center text-[11px] ${tone}`}>
            {marker.text} · {timeShort(t.created_at)}
          </span>
        </div>
      ),
    });
  }
  items.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));

  const convoBusy = busyId === convo.id;
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      {/* Header: who / where / state / controls */}
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-4 py-2.5 dark:border-gray-700">
        <button type="button" onClick={onBack} aria-label="Back to the conversation list" className={`${iconBtn} lg:hidden`}>
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0">
          <span className="flex items-center gap-2">
            <RowLink
              to={`/crm/contacts/${convo.contact_id}`}
              title="Open the full lead page (profile, screening, notes)"
              className="truncate text-sm font-semibold text-gray-900 hover:text-primary-700 dark:text-gray-100 dark:hover:text-primary-400"
            >
              {convo.contact_name || 'Lead'}
            </RowLink>
            <ExternalLink className="h-3 w-3 flex-shrink-0 text-gray-400" />
          </span>
          <span className="flex flex-wrap items-center gap-x-2 text-[11px] text-gray-500 dark:text-gray-400">
            {convo.contact_phone && <span>{convo.contact_phone}</span>}
            {currentSession?.study_name && <span className="truncate">{currentSession.study_name}</span>}
            {currentSession?.phase && <span>phase: {currentSession.phase}</span>}
          </span>
        </div>
        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_PILL[convo.status] || STATUS_PILL.closed}`}>
          {convo.status.replace('_', ' ')}{convo.pause_reason ? ` · ${convo.pause_reason.replace('_', ' ')}` : ''}{convo.close_reason ? ` · ${convo.close_reason.replace(/_/g, ' ')}` : ''}
        </span>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-700 dark:text-gray-300" title="copilot = every reply needs approval · auto = the agent sends within guardrails">
          {convo.mode_at_open}
        </span>
        {convo.upcoming_callback_at && (
          <span
            title={new Date(convo.upcoming_callback_at).toLocaleString()}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
          >
            <PhoneCall className="h-3 w-3" /> call {inFuture(convo.upcoming_callback_at)}
          </span>
        )}
        <span className="ml-auto inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
          {convo.status === 'active' && (
            <>
              <button type="button" aria-label="Pause the agent on this lead" title="Pause — the agent stops texting until resumed; the lead returns to normal recruiter surfaces" disabled={convoBusy} onClick={() => onConversationAction(convo.id, 'pause')} className={iconBtn}>
                <PauseCircle className="h-4 w-4" />
              </button>
              <button type="button" aria-label="Take over this conversation" title="Take over — the agent stops for good here and the thread is yours" disabled={convoBusy} onClick={() => onConversationAction(convo.id, 'takeover')} className={iconBtn}>
                <Hand className="h-4 w-4" />
              </button>
            </>
          )}
          {convo.status === 'paused' && (
            <button type="button" aria-label="Resume the agent on this lead" title="Resume — the agent picks the thread back up" disabled={convoBusy} onClick={() => onConversationAction(convo.id, 'resume')} className={iconBtn}>
              <PlayCircle className="h-4 w-4" />
            </button>
          )}
          {convo.status !== 'closed' && (
            <button
              type="button"
              aria-label="Close this conversation"
              title="Close — the agent stops here for good and the thread returns to the team"
              disabled={convoBusy}
              onClick={() => { setClosing((v) => !v); setCloseReason(''); }}
              className={`${iconBtn} !text-red-600 hover:!bg-red-50 dark:!text-red-400 dark:hover:!bg-red-900/20`}
            >
              <XCircle className="h-4 w-4" />
            </button>
          )}
        </span>
      </div>
      {closing && (
        <div className="flex flex-wrap items-center gap-2 border-b border-red-200 bg-red-50/60 px-4 py-2 dark:border-red-900/50 dark:bg-red-900/10">
          <span className="text-sm text-gray-700 dark:text-gray-200">
            Close this conversation? The agent stops here for good and the thread returns to the team.
          </span>
          <input
            value={closeReason}
            onChange={(e) => setCloseReason(e.target.value)}
            placeholder="Reason (optional)"
            aria-label="Close reason"
            className={`${fieldCls} min-w-0 flex-1`}
          />
          <button
            type="button"
            disabled={convoBusy}
            onClick={async () => { await onConversationAction(convo.id, 'close', closeReason.trim() ? { reason: closeReason.trim() } : undefined); setClosing(false); }}
            className={btnDanger}
          >
            <XCircle className="h-4 w-4" /> Close conversation
          </button>
          <button type="button" onClick={() => setClosing(false)} className={btnGhost}>Keep</button>
        </div>
      )}

      {/* Escalations for THIS thread — claim/resolve without leaving it */}
      {openEscalations.length > 0 && (
        <ul className="divide-y divide-amber-100 border-b border-amber-200 bg-amber-50/50 dark:divide-amber-900/40 dark:border-amber-900/50 dark:bg-amber-900/10">
          {openEscalations.map((e) => (
            <EscalationCard key={e.id} e={e} busy={busyId === e.id} nowTs={nowTs} onAction={onEscalationAction} />
          ))}
        </ul>
      )}

      {/* The two-way thread */}
      <div ref={threadRef} className="flex max-h-[52vh] min-h-[240px] flex-col gap-2 overflow-y-auto px-4 py-3 lg:max-h-[calc(100vh-380px)]">
        {items.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">No messages yet — the agent's opener may still be on its way.</p>
        ) : items.map((it) => <Fragment key={it.key}>{it.el}</Fragment>)}
      </div>

      {/* In-flight: approved but not yet sent — say why the thread looks idle */}
      {inFlight.length > 0 && (
        <div className="border-t border-gray-200 px-4 py-2 dark:border-gray-700">
          {inFlight.map((t) => {
            const hold = t.actions?.human_first?.release_at || null;
            const deferred = t.actions?.deferred_until || null;
            const label = hold && new Date(hold).getTime() > nowTs
              ? `held for a human until ${timeShort(hold)} — it sends itself if nobody steps in`
              : deferred && new Date(deferred).getTime() > nowTs
                ? `held for the morning window (quiet hours) — sends ${timeShort(deferred)}`
                : 'sending shortly (natural typing delay)';
            return (
              <p key={t.id} className="flex items-start gap-1.5 py-0.5 text-xs text-gray-500 dark:text-gray-400">
                <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary-500" />
                <span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Approved reply {label}:</span>{' '}
                  <span className="line-clamp-2">{t.final_body || t.draft_body}</span>
                </span>
              </p>
            );
          })}
        </div>
      )}

      {/* Pending drafts — approve right here */}
      {pendingDrafts.length > 0 && (
        <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50/60 p-3 dark:border-gray-700 dark:bg-gray-900/30">
          {pendingDrafts.map((d) => (
            <DraftCard key={d.id} t={d} busy={busyId === d.id} nowTs={nowTs} onApprove={onApprove} onReject={onReject} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The console page
// ---------------------------------------------------------------------------

export default function AgentConsole() {
  const [params, setParams] = useSearchParams();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [board, setBoard] = useState<BoardRow[]>([]);
  // v2 only: status=closed (limit 25) for the board's Recently-closed lane.
  // Classic never requests it; stale contents are never rendered.
  const [closedBoard, setClosedBoard] = useState<BoardRow[]>([]);
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const [suppressed, setSuppressed] = useState<DraftRow[]>([]);
  const [escalations, setEscalations] = useState<EscalationRow[]>([]);
  // KPI tile + Escalations tab badge (below): latched from the LAST
  // status='active' fetch only. `escalations` itself is view-dependent — the
  // Escalations tab's Needs action / Resolved chips flip escStatus and refill
  // this state with resolved rows — so a global count derived straight from
  // `escalations` dropped to 0 (or misread the 100-row cap as "100+ still
  // need action") while someone was just browsing history. null = no active
  // fetch has landed yet.
  // `opsCount` (D-7) is the non-queue-kind subset of the same latch — the part
  // the v2 Needs-you number adds on top of the queue items and drafts.
  const [activeEsc, setActiveEsc] = useState<{ count: number; capped: boolean; opsCount: number } | null>(null);
  // Review-queue tab badge (below): the same rows AgentReviewQueue itself
  // fetches from /agent/queue, kept here too so the badge is right even when
  // a different tab is open (buildQueueItems turns these into the dedup'd
  // work-item count the queue page actually lists).
  const [queueEscalations, setQueueEscalations] = useState<QueueEscalation[]>([]);
  const [queueReviews, setQueueReviews] = useState<QueueReview[]>([]);
  // v2 only: the queue endpoint's determinations (the delivery strip). The
  // response has always carried them; classic's queue child fetches its own.
  const [queueDeterminations, setQueueDeterminations] = useState<QueueDetermination[]>([]);
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [batchesAgentEnabled, setBatchesAgentEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [flagsOnly, setFlagsOnly] = useState(false);
  // The two lead-activity filters. Both answer the live grid's question rather
  // than the list's: which threads has the LEAD moved, not which exist.
  const [recentReplyOnly, setRecentReplyOnly] = useState(false);
  const [engagedOnly, setEngagedOnly] = useState(false);
  // C-15: the backend accepted mode/study_id filters all along — the UI sends them.
  const [modeFilter, setModeFilter] = useState<'' | 'copilot' | 'auto'>('');
  const [studyFilter, setStudyFilter] = useState('');
  const [studies, setStudies] = useState<StudyOption[]>([]);
  // 'active' = open + claimed (review M-12): claiming used to make a row
  // vanish — neither the open nor the resolved view listed it, so the claimer
  // could never find it to Resolve.
  const [escStatus, setEscStatus] = useState<'active' | 'resolved'>('active');
  const [busyId, setBusyId] = useState<string | null>(null);
  // Review L-10: a failed mutation used to log to the console and nothing
  // else — a lost approve race looked like a dead button. The strip names the
  // failure and the catch re-fetches so a stale card leaves the queue.
  const [actionError, setActionError] = useState<string | null>(null);
  // §7.1b: bumping this refetches the open workspace (realtime ping / after
  // any mutation) without remounting it.
  const [refreshTick, setRefreshTick] = useState(0);

  const fetchAll = useCallback(async () => {
    // Captured once per invocation (not read again after the await) so a
    // response that lands after the user has already flipped Needs action /
    // Resolved is tagged with the status it was ACTUALLY requested under —
    // see activeEsc above.
    const requestedEscStatus = escStatus;
    // The Recently-closed lane rides the same cycle as a concurrent,
    // fail-soft read. Caught immediately so a rejection can't surface as
    // unhandled if the main fan throws first.
    const closedPromise = api.get<{ conversations: BoardRow[] }>('/api/crm/agent/conversations', {
      params: { status: 'closed', limit: 25 },
    }).catch((err) => {
      console.error('[AgentConsole] closed-board fetch failed:', err);
      return null;
    });
    try {
      const [m, b, d, s, e, ba, q] = await Promise.all([
        api.get<Metrics>('/api/crm/agent/metrics'),
        api.get<{ conversations: BoardRow[] }>('/api/crm/agent/conversations', {
          params: {
            status: 'open',
            has_flags: flagsOnly ? 'true' : undefined,
            replied_within_minutes: recentReplyOnly ? RECENT_REPLY_MINUTES : undefined,
            engaged_only: engagedOnly ? 'true' : undefined,
            mode: modeFilter || undefined,
            study_id: studyFilter || undefined,
            limit: 200,
          },
        }),
        api.get<{ drafts: DraftRow[] }>('/api/crm/agent/drafts'),
        api.get<{ drafts: DraftRow[] }>('/api/crm/agent/drafts', { params: { status: 'suppressed' } }),
        api.get<{ escalations: EscalationRow[] }>('/api/crm/agent/escalations', { params: { status: escStatus } }),
        api.get<{ batches: BatchRow[]; agent_enabled?: boolean }>('/api/crm/agent/batches'),
        api.get<{ escalations: QueueEscalation[]; reviews: QueueReview[]; determinations?: QueueDetermination[] }>('/api/crm/agent/queue'),
      ]);
      setMetrics(m.data);
      setBoard(b.data?.conversations || []);
      setDrafts(d.data?.drafts || []);
      setSuppressed(s.data?.drafts || []);
      const escRows = e.data?.escalations || [];
      setEscalations(escRows);
      if (requestedEscStatus === 'active') {
        const active = escRows.filter((row) => row.status === 'open' || row.status === 'claimed');
        setActiveEsc({
          count: active.length,
          capped: escRows.length === 100,
          opsCount: active.filter((row) => !isQueueKind(row.kind)).length,
        });
      }
      setBatches(ba.data?.batches || []);
      setBatchesAgentEnabled(ba.data?.agent_enabled !== false);
      setQueueEscalations(q.data?.escalations || []);
      setQueueReviews(q.data?.reviews || []);
      setQueueDeterminations(q.data?.determinations || []);
      const c = await closedPromise;
      if (c) setClosedBoard(c.data?.conversations || []);
    } catch (err) {
      console.error('[AgentConsole] fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [flagsOnly, recentReplyOnly, engagedOnly, modeFilter, studyFilter, escStatus]);

  const failMsg = (err: any, verb: string) =>
    `${verb} failed: ${err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'request failed'}`;

  /**
   * The console's one update path (D-9). `reason === 'mount'` is the effect's
   * own load — the first one, and the re-run a filter change causes — and is
   * deliberately the only refetch that does NOT bump refreshTick, exactly as
   * the pre-D-9 effect behaved: the workspace and the queue child fetch
   * themselves on mount, and a tick there would be a duplicate request.
   */
  const refetchAll = useCallback((reason: FeedReason) => {
    const done = fetchAll();
    if (reason !== 'mount') setRefreshTick((n) => n + 1);
    return done;
  }, [fetchAll]);
  useAgentConsoleFeed(refetchAll);

  // C-15: the study filter's options (once — studies barely change).
  useEffect(() => {
    let alive = true;
    api.get('/api/crm/studies')
      .then((res) => {
        if (alive) setStudies((res.data?.studies || []).filter((s: StudyOption) => s.id && s.source === 'crm'));
      })
      .catch(() => { /* the filter just stays empty */ });
    return () => { alive = false; };
  }, []);

  // C-16: a coarse clock so SLA/hold chips count DOWN without waiting for the
  // next poll. Same 15s cadence, now off the ONE shared clock (D-9).
  const nowTs = useNow(15_000);

  const afterMutation = async () => { await fetchAll(); setRefreshTick((n) => n + 1); };

  const approve = async (t: DraftRow, text?: string) => {
    setBusyId(t.id);
    setActionError(null);
    try {
      await api.post(`/api/crm/agent/turns/${t.id}/approve`, text ? { text } : {});
      await afterMutation();
    } catch (err: any) {
      console.error('[AgentConsole] approve failed:', err);
      // §6.3b: the freshness gate refused — the lead texted past this draft.
      if (err?.response?.data?.error === 'stale_inbound') {
        setActionError('Not sent — the lead texted again after this draft was written. The agent is writing a fresh reply that takes their newest message into account.');
      } else {
        setActionError(failMsg(err, 'Approve'));
      }
      await afterMutation(); // a lost race means the card is stale — reconcile
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (t: DraftRow, reason: string) => {
    setBusyId(t.id);
    setActionError(null);
    try {
      await api.post(`/api/crm/agent/turns/${t.id}/reject`, { reason: reason.trim() || null });
      await afterMutation();
    } catch (err) {
      console.error('[AgentConsole] reject failed:', err);
      setActionError(failMsg(err, 'Reject'));
      await afterMutation();
    } finally {
      setBusyId(null);
    }
  };

  const conversationAction = async (
    id: string,
    action: 'pause' | 'resume' | 'takeover' | 'close',
    body?: { reason?: string },
  ) => {
    setBusyId(id);
    setActionError(null);
    try {
      await api.post(`/api/crm/agent/conversations/${id}/${action}`, body || {});
      await afterMutation();
    } catch (err) {
      console.error(`[AgentConsole] ${action} failed:`, err);
      setActionError(failMsg(err, action === 'pause' ? 'Pause' : action === 'resume' ? 'Resume' : action === 'close' ? 'Close' : 'Takeover'));
      await afterMutation();
    } finally {
      setBusyId(null);
    }
  };

  const escalationAction = async (id: string, action: 'claim' | 'resolve') => {
    setBusyId(id);
    setActionError(null);
    try {
      await api.post(`/api/crm/agent/escalations/${id}/${action}`);
      await afterMutation();
    } catch (err) {
      console.error(`[AgentConsole] escalation ${action} failed:`, err);
      setActionError(failMsg(err, action === 'claim' ? 'Claim' : 'Resolve'));
      await afterMutation();
    } finally {
      setBusyId(null);
    }
  };

  const cancelBatch = async (id: string) => {
    setBusyId(id);
    setActionError(null);
    try {
      await api.post(`/api/crm/agent/batches/${id}/cancel`);
      await fetchAll();
    } catch (err) {
      console.error('[AgentConsole] batch cancel failed:', err);
      setActionError(failMsg(err, 'Cancel batch'));
      await fetchAll();
    } finally {
      setBusyId(null);
    }
  };

  // C-19: batch drill-through — expand a row into its target list (with links
  // to the conversations it opened). Detail is keyed by batch id so a stale
  // response for a since-collapsed row renders nothing.
  const [openBatchId, setOpenBatchId] = useState<string | null>(null);
  const [batchDetail, setBatchDetail] = useState<{ id: string; targets: BatchTargetRow[] } | null>(null);
  const toggleBatch = async (id: string) => {
    if (openBatchId === id) { setOpenBatchId(null); setBatchDetail(null); return; }
    setOpenBatchId(id);
    setBatchDetail(null);
    try {
      const res = await api.get<{ targets: BatchTargetRow[] }>(`/api/crm/agent/batches/${id}`);
      setBatchDetail({ id, targets: res.data?.targets || [] });
    } catch (err) {
      console.error('[AgentConsole] batch detail failed:', err);
      setActionError(failMsg(err, 'Load batch'));
    }
  };

  return (
    <div>
      <PageHeader
        title="Agent console"
        icon={Bot}
        // The blurb is orientation for a first visit, not something a supervisor
        // re-reads — three wrapped lines on a phone is rent the queue should pay.
        subtitle={
          <span className="hidden sm:inline">
            Every agent conversation, draft, and escalation — humans supervise here.
          </span>
        }
        actions={<RefreshButton onClick={fetchAll} />}
      />

      {actionError && (
        <Alert tone="error" icon={false} className="mt-3" onDismiss={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      <ConsoleV2
        metrics={metrics}
        board={board}
        closedBoard={closedBoard}
        drafts={drafts}
        suppressed={suppressed}
        escalations={escalations}
        escStatus={escStatus}
        onEscStatusChange={setEscStatus}
        activeEsc={activeEsc}
        queueEscalations={queueEscalations}
        queueReviews={queueReviews}
        determinations={queueDeterminations}
        batches={batches}
        batchesAgentEnabled={batchesAgentEnabled}
        openBatchId={openBatchId}
        batchDetail={batchDetail}
        onToggleBatch={toggleBatch}
        studies={studies}
        loading={loading}
        nowTs={nowTs}
        busyId={busyId}
        refreshTick={refreshTick}
        params={params}
        setParams={setParams}
        onApprove={approve}
        onReject={reject}
        onConversationAction={conversationAction}
        onEscalationAction={escalationAction}
        onCancelBatch={cancelBatch}
        onError={setActionError}
        flagsOnly={flagsOnly}
        setFlagsOnly={setFlagsOnly}
        recentReplyOnly={recentReplyOnly}
        setRecentReplyOnly={setRecentReplyOnly}
        engagedOnly={engagedOnly}
        setEngagedOnly={setEngagedOnly}
        modeFilter={modeFilter}
        setModeFilter={setModeFilter}
        studyFilter={studyFilter}
        setStudyFilter={setStudyFilter}
        onRefresh={fetchAll}
      />
    </div>
  );
}
